import { useRef, useState } from 'react';
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import cn from './cn.js';
import Progress from './Progress.js';

export const ACCEPTED_DOCUMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/tiff',
  'image/heic',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
];

export const ACCEPTED_DOCUMENT_EXTENSIONS = '.pdf,.png,.jpg,.jpeg,.tiff,.tif,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt';

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Dropzone: drag & drop / click to pick files.
 * - `onFiles(files: File[])` receives already filtered files (by `accept` mime list).
 * - `hint` overrides the helper text.
 */
export default function Dropzone({ onFiles, accept = ACCEPTED_DOCUMENT_TYPES, acceptExtensions = ACCEPTED_DOCUMENT_EXTENSIONS, multiple = true, disabled = false, busy = false, className, hint, compact = false, children }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const filter = (list) => {
    const files = Array.from(list || []);
    if (!accept || accept.length === 0) return files;
    return files.filter((file) => accept.includes(file.type) || (!file.type && /\.(pdf|png|jpe?g|tiff?|heic|docx?|xlsx?|csv|txt)$/i.test(file.name)));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || busy) return;
    const files = filter(e.dataTransfer?.files);
    if (files.length) onFiles(files);
  };

  const handleChange = (e) => {
    const files = filter(e.target.files);
    e.target.value = '';
    if (files.length) onFiles(files);
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || busy}
      onClick={() => !disabled && !busy && inputRef.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && !busy) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled && !busy) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={cn(
        'group relative flex w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed text-center transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
        compact ? 'px-4 py-6' : 'px-6 py-10',
        dragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-gray-50/60 hover:border-indigo-300 hover:bg-indigo-50/40',
        (disabled || busy) && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <input ref={inputRef} type="file" className="sr-only" multiple={multiple} accept={acceptExtensions} onChange={handleChange} disabled={disabled || busy} tabIndex={-1} />
      {children || (
        <>
          <span className={cn('inline-flex items-center justify-center rounded-2xl bg-white text-indigo-600 ring-1 ring-gray-200 shadow-sm transition-transform duration-150 group-hover:-translate-y-0.5', compact ? 'h-10 w-10' : 'h-12 w-12')}>
            <CloudArrowUpIcon className={compact ? 'h-5 w-5' : 'h-6 w-6'} aria-hidden="true" />
          </span>
          <p className={cn('font-medium text-gray-900', compact ? 'mt-3 text-sm' : 'mt-4 text-sm')}>
            <span className="text-indigo-600">Click to upload</span> or drag and drop
          </p>
          <p className="mt-1 text-xs text-gray-500">{hint || 'PDF, images, Word, Excel, CSV or TXT'}</p>
        </>
      )}
    </div>
  );
}

/** List of selected / uploading files. */
export function FileList({ files = [], onRemove, progress, className, emptyText }) {
  if (!files.length) {
    return emptyText ? <p className={cn('text-sm text-gray-500', className)}>{emptyText}</p> : null;
  }
  return (
    <ul className={cn('divide-y divide-gray-100 overflow-hidden rounded-xl bg-white ring-1 ring-gray-200', className)}>
      {files.map((file, index) => {
        const pct = progress ? progress[index] : null;
        return (
          <li key={`${file.name}-${index}`} className="flex items-center gap-3 px-3.5 py-2.5">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <DocumentIcon className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                {file.size != null && <span>{formatBytes(file.size)}</span>}
                {pct != null && (
                  <span className="tabular-nums">{Math.round(pct)}%</span>
                )}
              </div>
              {pct != null && <Progress value={pct} size="sm" className="mt-1.5" tone={pct >= 100 ? 'success' : 'brand'} />}
            </div>
            {onRemove && pct == null && (
              <button type="button" onClick={() => onRemove(index)} className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-red-50 hover:text-red-600" aria-label={`Remove ${file.name}`}>
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
